import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import Headline from 'presentations/views/components/atoms/Headline';
import Link from 'presentations/views/components/atoms/Link';
import TopMainFeature from 'presentations/views/components/organisms/service/TopMainFeature';
import { setEvent } from 'presentations/views/helpers/analytics';
import {
  enterpriseInquiryPath,
  customerStoryListPath,
  homePath,
} from 'presentations/routes/path';
import {
  spacing,
  fontSize,
  mediaMax,
  borderRadius,
  fontWeight,
} from 'presentations/views/styles';
import {
  black,
  blueGrayPale,
  gray04,
  primary,
  white,
} from 'presentations/views/styles/color';
import { ProductType } from 'domains/models/blog_post/productType';
import { SyntheticEvent, useMemo } from 'react';
import { getLandingPageUrl } from '../OwnedContentBanner';
import Icon from 'presentations/views/components/atoms/Icon';
import CustomerListItem from 'presentations/views/components/molecules/CustomerStoryMediaListItem';
import { CustomerStoryListItem } from 'presentations/api/schema/contents';
import { useRouter } from 'next/router';

interface Props {
  productType: ProductType;
  customers: CustomerStoryListItem[];
}
export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const onClickTop = (e: SyntheticEvent) => {
    setEvent('click resource banner', {
      position: 'inline text',
      contentType: 'top',
    });
  };

  const onClickInquiry = (e: SyntheticEvent) => {
    setEvent('click corporative inquiry', {
      source: 'jicoo',
      product: 'resource',
      position: 'inline',
    });
  };

  const onClickStory = (e: SyntheticEvent) => {
    setEvent('click resource banner', {
      position: 'inline text',
      contentType: 'story',
    });
  };

  const title = t(`title_${props.productType}`);
  const description = t(`description_${props.productType}`);

  return (
    <>
      <InlineHeadline variant="h2">{t('Summary')}</InlineHeadline>
      <InlineSection>{t(`text_${props.productType}`)}</InlineSection>

      <About>
        <Title>{title}</Title>
        <Sentence>{description}</Sentence>
        <VideoContent>
          <TopMainFeature isHideNavigation />
        </VideoContent>
        <LinkButton>
          <LinkButtonItem
            href={homePath()}
            onClick={onClickTop}
            withoutNextLink
          >
            {t('Button')}
          </LinkButtonItem>
        </LinkButton>
        <EnterpriseInquiry
          href={enterpriseInquiryPath()}
          target="_blank"
          rel="noopener"
          onClick={onClickInquiry}
        >
          {t('Enterprise inquery')}
        </EnterpriseInquiry>
        {props.customers.length > 0 && (
          <CustomerListContainer>
            <CustomerListTitle variant="h3">
              {t('Customer stories')}
            </CustomerListTitle>
            <CustomerListText>{t('Customer stories desc')}</CustomerListText>
            <CustomerList count={props.customers.length}>
              {props.customers.map((customer) => (
                <CustomerListItemWrap key={customer.id}>
                  <CustomerListItem
                    customer={customer}
                    onClick={onClickStory}
                  />
                </CustomerListItemWrap>
              ))}
            </CustomerList>
            <MoreItem href={customerStoryListPath()}>
              <MoreIcon name="chevron-right" />
              <MoreName>{t('More')}</MoreName>
            </MoreItem>
          </CustomerListContainer>
        )}
        {/* <PromotionContainer>
          <PricingPromotion />
        </PromotionContainer> */}
      </About>
    </>
  );
}

const InlineHeadline = styled(Headline)`
  margin: 3rem 0 32px 0;
  font-size: ${fontSize(28)};
  line-height: 1.5;
  padding-bottom: ${spacing(24)};
  border-bottom: 1px solid ${primary};
`;
const InlineSection = styled.p`
  white-space: pre-wrap;
  letter-spacing: 0.05em;
  font-size: ${fontSize(16)};
  line-height: 1.8;
  margin-bottom: ${spacing(24)};
`;
const VideoContent = styled.div`
  width: 100%;
  padding: ${spacing(32)} 0;
  text-align: center;
`;

const About = styled.div`
  padding: ${spacing(32)};
  background: ${blueGrayPale};
  border-radius: ${borderRadius(16)};
  margin-bottom: ${spacing(32)};
  ${mediaMax.tablet} {
    font-size: ${fontSize(14)};
  }
`;
const Title = styled.h3`
  font-size: ${fontSize(20)};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(24)};
`;
const Sentence = styled.div`
  margin-bottom: ${spacing(24)};
  white-space: pre-wrap;
`;
const LinkButton = styled.div`
  display: flex;
  justify-content: center;
`;
const LinkButtonItem = styled(Link)`
  display: inline-block;
  border-radius: 50px;
  background: ${primary};
  color: ${white};
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(16)};
  text-align: center;
  padding: ${spacing(16)} ${spacing(32)};
  cursor: pointer;
`;
const EnterpriseInquiry = styled(Link)`
  color: ${primary} !important;
  margin-top: ${spacing(12)};
  font-size: ${fontSize(14)};
  display: block;
  font-weight: ${fontWeight('bold')};
  text-align: center;
`;

const CustomerList = styled.div<{ count: number }>`
  display: grid;
  grid-template-columns: ${(props) => `repeat(${props.count}, 1fr)`};
  column-gap: ${spacing(16)};
  row-gap: ${spacing(16)};
  padding-top: ${spacing(20)};
  ${mediaMax.tablet} {
    display: block;
  }
`;

const CustomerListItemWrap = styled.div`
  border-radius: ${borderRadius(16)};
  overflow: hidden;
  position: relative;
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(24)};
    :last-child {
      margin-bottom: 0;
    }
  }
`;

const MoreItem = styled(Link)`
  color: ${black};
  display: flex;
  align-items: center;
  margin-top: ${spacing(24)};
  justify-content: center;
`;
const MoreIcon = styled(Icon)`
  margin-right: ${spacing(4)};
  color: ${primary};
  width: 18px !important;
  height: 18px !important;
`;
const MoreName = styled.div``;
const CustomerListContainer = styled.div`
  border-top: 1px dotted ${gray04};
  padding-top: ${spacing(32)};
  margin-top: ${spacing(32)};
`;
const CustomerListTitle = styled(Headline)`
  text-align: center;
`;
const CustomerListText = styled.div`
  margin-top: ${spacing(16)};
  margin-bottom: ${spacing(16)};
  /* text-align: center; */
`;
